/**
 * AppJobsPage Component
 *
 * Main page for managing appJobs with search functionality.
 * Schedules define cron-based recurring execution of application functions.
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { AppJobTable } from '../components/AppJobTable';
import { CreateAppJobModal } from '../components/CreateAppJobModal';
import { AppJobDetailsModal } from '../components/AppJobDetailsModal';
import { UpdateAppJobModal } from '../components/UpdateAppJobModal';
import { DeleteAppJobModal } from '../components/DeleteAppJobModal';
import { TriggerAppJobModal } from '../components/TriggerAppJobModal';
import { ActivateAppJobModal } from '../components/ActivateAppJobModal';
import { useAppJobs } from '../hooks/useAppJobs';
import { usePauseAppJob } from '../hooks/useToggleAppJob';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/utils/permissions';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { AppJobSanitized } from '../../../types/app-job.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * AppJobsPage component
 */
export const AppJobsPage: React.FC = () => {
  const { user, tenant, activeRole } = useAuth();
  const layoutProps = useLayoutProps();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedAppJob, setSelectedAppJob] = useState<AppJobSanitized | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showTriggerModal, setShowTriggerModal] = useState<boolean>(false);
  const [showActivateModal, setShowActivateModal] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string>('');
  const [actionSuccess, setActionSuccess] = useState<string>('');

  const tenantId = tenant?.tenantId || '';

  // Permission checks
  const canCreate = hasPermission(activeRole?.permissions, 'app:app-job:create');
  const canUpdate = hasPermission(activeRole?.permissions, 'app:app-job:update');
  const canDelete = hasPermission(activeRole?.permissions, 'app:app-job:delete');
  const canTrigger = hasPermission(activeRole?.permissions, 'app:app-job:update');

  // Debounced search (300ms delay)
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch appJobs
  const {
    data: appJobs = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useAppJobs(tenantId);

  // Pause mutation
  const { mutate: pauseAppJob } = usePauseAppJob(tenantId, {
    onSuccess: () => {
      setActionSuccess('App job paused successfully');
      setTimeout(() => setActionSuccess(''), 3000);
    },
    onError: (error) => {
      setActionError(error);
      setTimeout(() => setActionError(''), 5000);
    },
  });

  // Filter appJobs locally
  const filteredAppJobs = useMemo(() => {
    if (!appJobs) return [];

    let filtered = [...appJobs];

    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (appJob) =>
          appJob.name.toLowerCase().includes(query) ||
          appJob.applicationCode.toLowerCase().includes(query) ||
          appJob.functionName.toLowerCase().includes(query) ||
          (appJob.description && appJob.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [appJobs, debouncedSearch]);

  const handleViewDetails = (appJob: AppJobSanitized) => {
    setSelectedAppJob(appJob);
    setShowDetailsModal(true);
  };

  const handleEdit = (appJob: AppJobSanitized) => {
    setSelectedAppJob(appJob);
    setShowEditModal(true);
  };

  const handleDelete = (appJob: AppJobSanitized) => {
    setSelectedAppJob(appJob);
    setShowDeleteModal(true);
  };

  const handlePause = (appJob: AppJobSanitized) => {
    pauseAppJob(appJob.appJobId);
  };

  const handleResume = (appJob: AppJobSanitized) => {
    setSelectedAppJob(appJob);
    setShowActivateModal(true);
  };

  const handleTrigger = (appJob: AppJobSanitized) => {
    setSelectedAppJob(appJob);
    setShowTriggerModal(true);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>App Jobs</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Manage cron-based recurring function executions
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreateModal(true)}>Create App Job</Button>
          )}
        </div>

        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search app jobs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Action Alerts */}
        {actionError && (
          <Alert variant="error" onClose={() => setActionError('')}>
            {actionError}
          </Alert>
        )}
        {actionSuccess && (
          <Alert variant="success" onClose={() => setActionSuccess('')}>
            {actionSuccess}
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {getErrorMessage(error)}
          </Alert>
        )}

        {/* Schedules Table */}
        <AppJobTable
          appJobs={filteredAppJobs}
          isLoading={isLoading}
          onView={handleViewDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onPause={handlePause}
          onResume={handleResume}
          onTrigger={handleTrigger}
          canEdit={canUpdate}
          canDelete={canDelete}
          canTrigger={canTrigger}
        />

        {/* Modals */}
        <CreateAppJobModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          tenantId={tenantId}
        />

        {showDetailsModal && selectedAppJob && (
          <AppJobDetailsModal
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedAppJob(null);
            }}
            tenantId={tenantId}
            appJobId={selectedAppJob.appJobId}
          />
        )}

        {showEditModal && selectedAppJob && (
          <UpdateAppJobModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedAppJob(null);
            }}
            tenantId={tenantId}
            appJob={selectedAppJob}
          />
        )}

        {showDeleteModal && selectedAppJob && (
          <DeleteAppJobModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedAppJob(null);
            }}
            tenantId={tenantId}
            appJob={selectedAppJob}
          />
        )}

        {showTriggerModal && selectedAppJob && (
          <TriggerAppJobModal
            isOpen={showTriggerModal}
            onClose={() => {
              setShowTriggerModal(false);
              setSelectedAppJob(null);
            }}
            tenantId={tenantId}
            appJob={selectedAppJob}
          />
        )}

        {showActivateModal && selectedAppJob && (
          <ActivateAppJobModal
            isOpen={showActivateModal}
            onClose={() => {
              setShowActivateModal(false);
              setSelectedAppJob(null);
            }}
            tenantId={tenantId}
            appJob={selectedAppJob}
            onSuccess={() => {
              setActionSuccess('App job activated successfully');
              setTimeout(() => setActionSuccess(''), 3000);
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
